'use client';
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { User, Session } from '@supabase/supabase-js';

interface UserProfile {
  id: string
  fullName?: string
  email?: string
  avatarUrl?: string
  role?: string
  [key: string]: any
}

interface AuthContextType {
  user: User | null
  userProfile: UserProfile | null
  loading: boolean
  profileLoading: boolean
  signIn: (email: string, password: string) => Promise<any>
  signOut: () => Promise<any>
  updateProfile: (updates: Partial<UserProfile>) => Promise<any>
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | null>(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  const loadProfile = async (userId: string) => {
    if (!userId) return
    setProfileLoading(true)
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single()
      
      if (error) {
        // If profile doesn't exist, try to create it from auth user data
        if (error.code === 'PGRST116') {
          // Get auth user data
          const { data: { user: authUser } } = await supabase.auth.getUser()
          if (authUser) {
            // Attempt to create profile
            const { data: newProfile, error: createError } = await supabase
              .from('user_profiles')
              .insert({
                id: authUser.id,
                email: authUser.email,
                full_name: authUser.user_metadata?.full_name || authUser.email,
                role: authUser.user_metadata?.role || 'candidate',
                is_active: true
              })
              .select()
              .single()
            
            if (!createError && newProfile) {
              const profile: UserProfile = {
                id: newProfile.id,
                fullName: newProfile.full_name,
                email: newProfile.email,
                avatarUrl: newProfile.avatar_url,
                role: newProfile.role,
                phone: newProfile.phone,
                timezone: newProfile.timezone,
                isActive: newProfile.is_active,
              }
              setUserProfile(profile)
              return
            } else {
              // Create a minimal profile from auth data if database insert fails
              const fallbackProfile: UserProfile = {
                id: authUser.id,
                fullName: authUser.user_metadata?.full_name || authUser.email || 'User',
                email: authUser.email || '',
                role: authUser.user_metadata?.role || 'candidate',
                isActive: true,
              }
              setUserProfile(fallbackProfile)
              return
            }
          }
        }
        // For any other error, create fallback profile from session user
        const { data: { user: sessionUser } } = await supabase.auth.getUser()
        if (sessionUser) {
          const fallbackProfile: UserProfile = {
            id: sessionUser.id,
            fullName: sessionUser.user_metadata?.full_name || sessionUser.email || 'User',
            email: sessionUser.email || '',
            role: sessionUser.user_metadata?.role || 'candidate',
            isActive: true,
          }
          setUserProfile(fallbackProfile)
        }
        return
      }
      
      if (data) {
        const profile: UserProfile = {
          id: data.id,
          fullName: data.full_name,
          email: data.email,
          avatarUrl: data.avatar_url,
          role: data.role,
          phone: data.phone,
          timezone: data.timezone,
          isActive: data.is_active,
        }
        setUserProfile(profile)
      }
    } catch (error: any) {
      // Create fallback profile from session user if anything fails
      try {
        const { data: { user: sessionUser } } = await supabase.auth.getUser()
        if (sessionUser) {
          const fallbackProfile: UserProfile = {
            id: sessionUser.id,
            fullName: sessionUser.user_metadata?.full_name || sessionUser.email || 'User',
            email: sessionUser.email || '',
            role: sessionUser.user_metadata?.role || 'candidate',
            isActive: true,
          }
          setUserProfile(fallbackProfile)
        }
      } catch (fallbackError) {
        setUserProfile(null)
      }
    } finally {
      setProfileLoading(false)
    }
  }

  const clearProfile = () => {
    setUserProfile(null)
    setProfileLoading(false)
  }

  const handleAuthStateChange = (_event: string | null, session: Session | null) => {
    setUser(session?.user ?? null)
    setLoading(false)
    if (session?.user) {
      loadProfile(session.user.id)
    } else {
      clearProfile()
    }
  }

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      handleAuthStateChange(null, session)
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(handleAuthStateChange)

    return () => subscription?.unsubscribe()
  }, [])

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) {
        if (error.message.includes('Failed to fetch') || 
            error.message.includes('AuthRetryableFetchError')) {
          return { 
            data: null, 
            error: { 
              message: 'Cannot connect to authentication service. Your Supabase project may be paused or inactive. Please check your Supabase dashboard and resume your project if needed.' 
            } 
          }
        }
        return { data: null, error: { message: error.message } }
      }
      return { data, error: null }
    } catch (error: any) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        return { 
          data: null, 
          error: { 
            message: 'Cannot connect to authentication service. Please check your internet connection.' 
          } 
        }
      }
      return { data: null, error: { message: 'Sign in failed. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut()
      if (!error) {
        setUser(null)
        clearProfile()
      }
      return { error: error ? { message: error.message } : null }
    } catch (error: any) {
      return { error: { message: 'Sign out failed. Please try again.' } }
    }
  }

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) return { error: { message: 'No user logged in' } }
    try {
      const dbUpdates: any = {}
      if (updates.fullName !== undefined) dbUpdates.full_name = updates.fullName
      if (updates.avatarUrl !== undefined) dbUpdates.avatar_url = updates.avatarUrl
      if (updates.phone !== undefined) dbUpdates.phone = updates.phone
      if (updates.timezone !== undefined) dbUpdates.timezone = updates.timezone
      
      const { data, error } = await supabase
        .from('user_profiles')
        .update(dbUpdates)
        .eq('id', user.id)
        .select()
        .single()
      
      if (!error && data) {
        const profile: UserProfile = {
          id: data.id,
          fullName: data.full_name,
          email: data.email,
          avatarUrl: data.avatar_url,
          role: data.role,
          phone: data.phone,
          timezone: data.timezone,
          isActive: data.is_active,
        }
        setUserProfile(profile)
      }
      return { data, error: error ? { message: error.message } : null }
    } catch (error: any) {
      return { error: { message: 'Profile update failed. Please try again.' } }
    }
  }

  const value: AuthContextType = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signOut,
    updateProfile,
    isAuthenticated: !!user,
  }

  return <AuthContext.Provider data-component-id="src/contexts/AuthContext.tsx:263:9" data-component-path="src/contexts/AuthContext.tsx" data-component-line="263" data-component-end-line="263" data-component-file="AuthContext.tsx" data-component-name="AuthContext.Provider" data-component-content="%7B%22elementName%22%3A%22AuthContext.Provider%22%2C%22value%22%3A%22%5Bvar%3Avalue%5D%22%7D" value={value}>{children}</AuthContext.Provider>
}